import {
  Form,
  Heading,
  Text,
  PhoneField,
  TextField,
  SingleSelectField,
  NumberField,
  HStack,
  Box,
  CircularProgress,
  SubmitButton,
  VStack,
} from '@medely/ui-kit';
import React from 'react';
import { personalValidationSchema } from './personalValidationSchema';
import { usePersonalFormData } from './usePersonalFormData';
import { PersonalFormValuesProps } from './types';
import { usePersonalFormSubmit } from './usePersonalFormSubmit';
import { FixedFooterLayout } from '@mp/components';

export const PersonalForm = () => {
  const { defaultValues, loadingData, stateOptions } = usePersonalFormData();
  const { createEmergencyContact, isSubmitting, onSubmitAccount, updateEmergencyContact } =
    usePersonalFormSubmit();
  const fixedFooterLayoutHeight = '92%';
  const hasNewEmergencyContactData = (
    values: PersonalFormValuesProps,
    initialValues: typeof defaultValues,
  ) => {
    const hasNewFirstName = values.emergency_first_name !== initialValues.emergency_first_name;
    const hasNewLastName = values.emergency_last_name !== initialValues.emergency_last_name;
    const hasNewPhone = values.emergency_phone_number !== initialValues.emergency_phone_number;

    return hasNewFirstName || hasNewLastName || hasNewPhone;
  };

  const createOrUpdateEmergencyContact = ({ values }: { values: PersonalFormValuesProps }) => {
    const hasNewEmergencyData = hasNewEmergencyContactData(values, defaultValues);
    if (hasNewEmergencyData && values.emergency_id) {
      updateEmergencyContact(values);
    } else if (hasNewEmergencyData) {
      createEmergencyContact(values);
    }
  };

  const handleFormSubmit = (values: PersonalFormValuesProps) => {
    const hasNewEmergencyData = hasNewEmergencyContactData(values, defaultValues);
    if (hasNewEmergencyData) {
      createOrUpdateEmergencyContact({ values });
    }
    onSubmitAccount(values);
  };

  if (loadingData) {
    return <CircularProgress />;
  }

  return (
    <Form
      handleSubmit={handleFormSubmit}
      schema={personalValidationSchema}
      defaultValues={defaultValues}
    >
      <Box testId="personal-form">
        <Heading size="s" testId="personal-personal-information-title">
          Personal information
        </Heading>
        <Box py={1} />
        <FixedFooterLayout
          height={fixedFooterLayoutHeight}
          footerContent={
            <SubmitButton
              loading={isSubmitting}
              disabled={isSubmitting}
              fullWidth
              testId="personal-form-save-button"
            >
              Save
            </SubmitButton>
          }
        >
          <VStack gap={1}>
            <TextField name="nickname" label="Preferred name" testId="personal-form-nickname" />
            <PhoneField
              name="phone_number"
              label="Phone number*"
              testId="personal-form-phone-number"
            />
            <Box py={1} />
            <Heading size="s" testId="personal-home-address-title">
              Home address
            </Heading>
            <Text size="m" testId="personal-home-address-subtitle">
              Update your address to get matched to local per-diem shifts and assignments.
            </Text>
            <TextField
              name="address_first"
              label="Street Address*"
              testId="personal-form-address-first"
            />
            <TextField
              name="address_second"
              label="Unit, Suite, Building number"
              testId="personal-form-address-second"
            />
            <TextField name="address_city" label="City*" testId="personal-form-address-city" />
            <HStack width="100%">
              <Box width="48%" testId="personal-form-state-id">
                <SingleSelectField name="state_id" label="State*" options={stateOptions} />
              </Box>
              <Box width="4%" />
              <Box width="48%">
                <NumberField
                  name="address_zip"
                  label="Zip code*"
                  testId="personal-form-address-zip"
                />
              </Box>
            </HStack>
            <Box py={1} />
            <Heading size="s" testId="personal-emergency-contact-title">
              Emergency contact
            </Heading>
            <HStack width="100%">
              <Box width="48%">
                <TextField
                  name="emergency_first_name"
                  label="First name*"
                  testId="personal-emergency-contact-first-name"
                />
              </Box>
              <Box width="4%" />
              <Box width="48%">
                <TextField
                  name="emergency_last_name"
                  label="Last name*"
                  testId="personal-emergency-contact-last-name"
                />
              </Box>
            </HStack>
            <PhoneField
              name="emergency_phone_number"
              label="Phone number*"
              testId="emergency-phone-number"
            />
            <Box py={2} />
          </VStack>
        </FixedFooterLayout>
      </Box>
    </Form>
  );
};
